// canalAbierto.js — lo que entra por el buzón.
//
// Contrato: `docs/CANAL_ABIERTO.md` §4. El servidor lo está construyendo; este
// servicio se escribe contra la forma acordada y **tolera que aún no exista**:
// mientras el endpoint devuelva 404, `getEntradas()` da un array vacío y la
// página enseña solo la convocatoria. El día que exista, se llena sola.
//
// Mismo patrón que `actualidad.js` y `tvVideos.js`: un JSON estático público,
// caché de cinco minutos y petición compartida.
//
// ⚠️ Del contrato:
//   · `entradas` es un **array**, ya ordenado por `arrivedAt` descendente.
//   · Hay **borradores** dentro: solo se pinta `status: "published"`.
//   · `cover` es ruta relativa y puede ser `null`.
//   · `programme` es la **clave** de `programas.json` —`hcr`, `romanticos`—,
//     no el nombre visible. Nocturna se indexa como `romanticos`.

import { LISTEN } from "./host";

const BASE = `${LISTEN}`;

// Renombrado el 31.08.26, cuando la sección pasó a llamarse Radar. El servidor
// mantiene `/canal-abierto.json` como alias 1:1 y no lo retirará hasta que web y
// app confirmen que no lo usan; se usa el nuevo para poder confirmarlo.
const URL_CANAL = `${BASE}/radar.json`;
const TTL = 5 * 60 * 1000;

let cache = null;
let cacheAt = 0;
let enVuelo = null;

/**
 * Olvida lo guardado y obliga a preguntar de nuevo.
 *
 * Hace falta porque la caché de arriba vive en el módulo: sobrevive a cambiar
 * de vista y solo caduca por tiempo. Sin esto, publicar algo y volver a la
 * pestaña no servía de nada — se seguía sirviendo lo aprendido hace cuatro
 * minutos.
 */
export function invalidar() {
  cache = null;
  cacheAt = 0;
}

const MESES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

/** «2026-08-30» → «30 de agosto de 2026». */
function enLargo(fecha) {
  const [y, m, d] = (fecha || "").slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return "";
  return `${d} de ${MESES[m - 1]} de ${y}`;
}

// Cómo se dice cada formato en pantalla. La clave es la del contrato.
const FORMATOS = {
  lp: "LP",
  ep: "EP",
  single: "Single",
  maqueta: "Maqueta",
};

/**
 * Ruta relativa del contrato → URL absoluta. Vacía si no hay.
 *
 * 🔴 **Se usa la ruta tal cual llega, sin tocar el prefijo.** El campo `cover`
 * sigue viniendo como `/canal-abierto/images/…` aunque la sección ya se llame
 * Radar: es dato persistido, y el servidor sirve la misma imagen por los dos
 * prefijos. Reescribirlo aquí a `/radar/images/…` sería adivinar, y el día que
 * cambien los datos guardados nos quedaríamos sin carátulas sin saber por qué.
 */
function url(ruta) {
  if (!ruta) return "";
  return ruta.startsWith("http") ? ruta : BASE + ruta;
}

// Los logotipos que hay hoy en `AstraIcon`. Si el catálogo añade una plataforma
// —Mixcloud, TuneIn, Apple Music—, basta con pegar su glifo con ese nombre y
// añadirlo aquí.
const GLIFOS = [
  "bandcamp",
  "spotify",
  "soundcloud",
  "youtube",
  "instagram",
  "facebook",
  "bluesky",
  "x",
  "website",
];

function icono(label = "") {
  const clave = label
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z]/g, "");
  // «Web oficial» y «Web propia» son la misma casa.
  const nombre = clave.startsWith("web") ? "website" : clave;
  return GLIFOS.includes(nombre) ? nombre : "link";
}

// Forma única para la web. Las vistas no conocen los nombres del contrato.
function normaliza(e) {
  const generos = Array.isArray(e.genres) ? e.genres.filter(Boolean) : [];
  const lugar = [e.city, e.country].filter(Boolean).join(", ");

  return {
    slug: e.slug || "",
    // 🔴 **El slug del artista, que el contrato manda y aquí se tiraba.**
    // `slug` es del trabajo —artista + título— y `artistSlug` es de la persona.
    // Sin copiarlo, `/radar/artista/<slug>` solo resolvía por el trabajo: la
    // dirección de un artista cambiaba con lo último que mandara, y el roster
    // del área, que enlaza con el slug bueno, no llegaba a ninguna parte.
    artistSlug: e.artistSlug || "",
    artist: e.artist || "",
    title: e.title || "",

    city: e.city || "",
    country: e.country || "",
    // Para el raíl y el pie de la celda: «Rosario, Argentina».
    place: lugar,

    format: e.format || "",
    // El primero es el que va de kicker; el resto, al raíl. Igual que
    // `programmes.js` con `generos` / `genre`.
    genres: generos,
    genre: generos[0] || "",

    formatLabel: FORMATOS[e.format] || e.format || "",
    label: e.label || "",
    year: e.year || null,

    cover: url(e.cover),
    description: e.description || "",
    featuredTrack: e.featuredTrack || "",
    // La URL del tema destacado, que **ya la manda el servidor** desde el
    // 01.09.26 como `featuredTrackUrl`. Se acepta con dos nombres porque el
    // contrato tardó en elegir el suyo.
    //
    // 🔴 **Por `url()`, como la portada.** Llega relativa —`/canal-abierto/audio/
    // …mp3`— y se estaba pasando cruda al `<audio>`: el navegador la resolvía
    // contra `astra.fm`, donde no hay ficheros sino una SPA, así que el servidor
    // devolvía su `index.html` con un 200 y el reproductor recibía HTML donde
    // esperaba un mp3. Se veía como «el audio no suena» sin ningún error.
    track: url(e.track || e.featuredTrackUrl),
    listen: e.listen || "",
    // Cada enlace con su glifo. En Radio no hace falta mapa porque la clave del
    // contrato ya es el nombre del icono; aquí el contrato manda `label`
    // —«Spotify», «Bandcamp», «Youtube»—, así que se normaliza a minúsculas y se
    // usa igual. Lo que no tiene glifo cae en `link`, que es el genérico.
    links: Array.isArray(e.links)
      ? e.links.filter((l) => l?.url).map((l) => ({ ...l, icon: icono(l.label) }))
      : [],

    gigs: Array.isArray(e.gigs) ? e.gigs : [],
    next: e.next || "",

    arrivedAt: (e.arrivedAt || "").slice(0, 10),
    arrivedLong: enLargo(e.arrivedAt),

    // Los tres que cierran el círculo: entró, en qué programa y desde cuándo.
    onAir: Boolean(e.onAir),
    programme: e.programme || "",
    firstPlayedAt: (e.firstPlayedAt || "").slice(0, 10),
    firstPlayedLong: enLargo(e.firstPlayedAt),

    // 🔴 `arrivedAt` es solo el DÍA, y dos materiales del mismo día empatan.
    // `sort` es estable, así que al empatar mandaba el orden del servidor y
    // delante quedaba el más antiguo: la portada de la casa enseñaba el primero
    // de los dos en vez del último publicado. `createdAt` es la marca real de
    // publicación y desempata; concatenados, un orden lexicográfico basta.
    _orden: `${e.arrivedAt || ""}|${e.createdAt || ""}`,

    // Otros slugs que llevan a este mismo disco, si hubo duplicados.
    _alias: [],
  };
}

// ── El mismo disco, dos veces ──
//
// El buzón puede acabar con dos registros del mismo material: se sube, se
// corrige, se vuelve a subir. Para el contrato son dos entradas con `slug`
// distinto; para quien mira la página son el mismo disco, y verlo dos veces
// —una con portada y otra sin— no se entiende desde fuera.
//
// Pasó con «Footings» de catch92: la portada quedó en un registro y el
// «en parrilla» en el otro, así que ninguna de las dos fichas estaba completa
// y cuál se veía dependía de por dónde entraras.
//
// Se resuelve aquí y no en cada vista: el servicio es la fuente única, así que
// la sección, la portada de la casa, la ficha y «Más del canal» heredan la
// misma decisión sin ponerse de acuerdo.

/** Artista + título, sin mayúsculas, acentos ni espacios de más. */
function clave(e) {
  return `${e.artist}|${e.title}`
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

const vacio = (v) =>
  v == null || v === "" || (Array.isArray(v) && v.length === 0);

/**
 * Funde `otro` DENTRO de `base` sin pisar nada: `base` es el registro más
 * reciente y manda, y del viejo solo se toma lo que al nuevo le falte. Así la
 * ficha enseña la portada aunque se subiera en el primer intento.
 */
function funde(base, otro) {
  for (const [k, v] of Object.entries(otro)) {
    if (k.startsWith("_")) continue;
    if (vacio(base[k]) && !vacio(v)) base[k] = v;
  }
  // Si sonó en cualquiera de los dos, sonó.
  base.onAir = Boolean(base.onAir || otro.onAir);
  // El slug del registro absorbido sigue siendo una URL válida: puede estar
  // compartida por ahí, y no va a llevar a un 404.
  base._alias.push(otro.slug, ...otro._alias);
  return base;
}

/** Lista ya ordenada → lista sin duplicados del mismo disco. */
function deduplica(lista) {
  const porClave = new Map();
  for (const e of lista) {
    const k = clave(e);
    const previo = porClave.get(k);
    if (previo) funde(previo, e);
    else porClave.set(k, e);
  }
  return [...porClave.values()];
}

// ── El retrato del artista ──
//
// 🔴 **`imagen` es la foto del artista y `portada` es la del disco.** Hasta el
// 03.09.26 `radar.json` solo traía `cover`, la portada del trabajo, y las dos
// pantallas de Radar la usaban como retrato «mientras el contrato no traiga
// `artistImage`». Estaba pedido y ha llegado, en un fichero hermano:
//
//     GET https://listen.astra.fm/radar/artistas.json
//     { updatedAt, artistas: [{ artistSlug, nombre, bio, entradas,
//                               imagen, portada }] }
//
// El aviso del servidor lo dice con todas las letras: «si usabais `imagen` como
// retrato, ahora es correcto de verdad; si queréis ilustrar cuando no hay foto,
// tirad de `portada`». Eso es exactamente lo que hace `retratoDe`.
//
// ⚠️ **`imagen` puede venir `null`**, y hoy viene así en los cuatro artistas: la
// redacción todavía no ha subido ninguna foto. Así que esto no cambia nada de lo
// que se ve hoy — cambia el día que suban la primera, y entonces aparece sola.
//
// Este fichero **no sustituye a `radar.json`**: la sección sigue leyendo de allí
// los trabajos. De aquí sale solo el retrato, así que si falla no se pierde nada.
const URL_ARTISTAS = `${BASE}/radar/artistas.json`;

let cacheArtistas = null;
let cacheArtistasAt = 0;
let artistasEnVuelo = null;

/**
 * Los artistas por `artistSlug`. Nunca lanza: sin este fichero, las pantallas
 * siguen ilustrando con la portada, que es lo que hacían antes.
 */
export async function getArtistas() {
  const ahora = Date.now();
  if (cacheArtistas && ahora - cacheArtistasAt < TTL) return cacheArtistas;
  if (artistasEnVuelo) return artistasEnVuelo;

  artistasEnVuelo = (async () => {
    try {
      const r = await fetch(URL_ARTISTAS);
      if (!r.ok) throw new Error(String(r.status));
      const data = await r.json();

      const mapa = new Map();
      for (const a of data?.artistas || []) {
        if (!a?.artistSlug) continue;
        mapa.set(a.artistSlug, {
          slug: a.artistSlug,
          nombre: a.nombre || "",
          bio: a.bio || "",
          // La foto del artista. Vacía si la redacción no ha subido ninguna.
          imagen: url(a.imagen),
          // La portada de su trabajo, para ilustrar mientras no haya foto.
          portada: url(a.portada),
        });
      }

      cacheArtistas = mapa;
      cacheArtistasAt = ahora;
      return cacheArtistas;
    } catch (error) {
      return cacheArtistas || new Map();
    } finally {
      artistasEnVuelo = null;
    }
  })();

  return artistasEnVuelo;
}

/**
 * Con qué se ilustra a un artista: **su foto si la hay, y si no una portada**.
 *
 * @param {Map} artistas  lo que devuelve `getArtistas()`
 * @param {string} slug   el `artistSlug`
 * @param {string} respaldo  la portada que ya tenía la pantalla a mano
 */
export function retratoDe(artistas, slug, respaldo = "") {
  const a = artistas?.get?.(slug);
  return a?.imagen || a?.portada || respaldo;
}

/**
 * Lo publicado, de lo más nuevo a lo más viejo.
 * Nunca lanza: si el endpoint no existe todavía —o la red falla— devuelve un
 * array vacío y la página se queda con la convocatoria, que es lo que importa.
 */
export async function getEntradas() {
  const ahora = Date.now();
  if (cache && ahora - cacheAt < TTL) return cache;
  if (enVuelo) return enVuelo;

  enVuelo = (async () => {
    try {
      const r = await fetch(URL_CANAL);
      if (!r.ok) throw new Error(String(r.status));
      const data = await r.json();

      const lista = (data?.entradas || [])
        .filter((e) => e?.status === "published" && e?.slug)
        .map(normaliza)
        .sort((a, b) => b._orden.localeCompare(a._orden));

      cache = deduplica(lista);

      cacheAt = ahora;
      return cache;
    } catch (error) {
      // Todavía no existe, o la red se cayó. Ni una cosa ni la otra son un
      // error que deba ver nadie: la sección tiene sentido sin datos.
      return cache || [];
    } finally {
      enVuelo = null;
    }
  })();

  return enVuelo;
}

/** Una entrada por su slug, o `null`. */
export async function getEntrada(slug) {
  if (!slug) return null;
  const lista = await getEntradas();
  return (
    lista.find((e) => e.slug === slug) ||
    lista.find((e) => e._alias.includes(slug)) ||
    null
  );
}

// ── El envío ───────────────────────────────────────────────────────────────

// Contrato §4. ⚠️ NO es `/api/radar`, que es lo que la web propuso en su día: el
// servidor lo publicó en `/canal-abierto/submit`, hoy `/radar/submit`.
// Comprobado contra el
// servidor real el 26.08.26.
const URL_ENVIO = `${BASE}/radar/submit`;

/**
 * Manda una candidatura al buzón privado del estudio.
 *
 * 🔴 **No publica nada.** El envío entra en el buzón interno; la redacción lo
 * escucha y, si lo pone, crea la ficha. Nada de lo que entra por aquí aparece
 * en la web sin pasar por ahí — y eso hay que contarlo en la confirmación, o
 * la banda se queda esperando ver su ficha.
 *
 * @returns {Promise<{ok: boolean, id?: string, motivo?: string}>} Nunca lanza:
 * quien lo llama necesita poder ofrecer el correo como alternativa, no un
 * stack trace. Devuelve el `id` para poder subir la carátula después.
 */
export async function enviarCandidatura(datos) {
  try {
    const r = await fetch(URL_ENVIO, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(datos),
    });

    if (r.ok) {
      // El id hace falta para el segundo paso: la carátula se sube contra él.
      const data = await r.json().catch(() => null);
      return { ok: true, id: data?.data?.id || "" };
    }

    // El servidor limita a **diez peticiones por hora y por IP**, contando
    // envíos y carátulas juntos. No es un fallo ni se arregla reintentando:
    // hay que decirlo con esas palabras, porque «no hemos podido enviarlo»
    // invita a volver a intentarlo ya.
    if (r.status === 429) return { ok: false, motivo: "demasiados" };

    // 400: al servidor le falta algo. Su mensaje dice EXACTAMENTE qué, así que
    // se devuelve tal cual en vez de un «faltan datos» genérico: el formulario
    // ya valida lo que sabe, de modo que un 400 significa que el contrato se
    // movió, y ahí el texto del servidor es lo único que orienta.
    if (r.status === 400) {
      const cuerpo = await r.json().catch(() => null);
      return { ok: false, motivo: "faltan-datos", detalle: cuerpo?.error || "" };
    }

    return { ok: false, motivo: "servidor" };
  } catch (error) {
    // Sin red o CORS: para quien envía es lo mismo.
    return { ok: false, motivo: "sin-conexion" };
  }
}


// ── La carátula, que es un segundo paso ────────────────────────────────────

// Contrato §4: hasta 8 MB, cualquier formato de imagen. El servidor la recorta
// cuadrada y la optimiza a 800px, así que aquí no se toca el fichero.
export const CARATULA_MAX_BYTES = 8 * 1024 * 1024;

/**
 * Sube la carátula de un envío ya registrado.
 *
 * 🔴 **Va DESPUÉS del 201 y contra el `id` que devolvió.** Y si falla, el envío
 * del paso 1 **ya está hecho**: no se reintenta el envío entero ni se le dice a
 * nadie que no llegó — llegó, y lo que falta es la imagen. Repetir el paso 1
 * duplicaría la candidatura en el buzón de la redacción.
 *
 * El cuerpo es el fichero CRUDO, no `FormData`: el `Content-Type` es el del
 * propio fichero.
 *
 * @returns {Promise<{ok: boolean, motivo?: string}>} Nunca lanza.
 */
export async function subirCaratula(id, fichero) {
  if (!id || !fichero) return { ok: false, motivo: "sin-datos" };

  try {
    const r = await fetch(`${URL_ENVIO}/${encodeURIComponent(id)}/cover`, {
      method: "POST",
      headers: { "Content-Type": fichero.type || "application/octet-stream" },
      body: fichero,
    });

    if (r.ok) return { ok: true };
    // 404: el id no existe o la redacción ya revisó el envío.
    if (r.status === 404) return { ok: false, motivo: "no-encontrado" };
    if (r.status === 429) return { ok: false, motivo: "demasiados" };
    // 500: no era una imagen válida, pese a lo que dijera su extensión.
    if (r.status === 500) return { ok: false, motivo: "no-es-imagen" };
    return { ok: false, motivo: "servidor" };
  } catch (error) {
    return { ok: false, motivo: "sin-conexion" };
  }
}


// ── El audio, que es un tercer paso ────────────────────────────────────────

// Contrato v2 (27.08.26). El v1 decía que NO se aceptaban ficheros de audio;
// el servidor cambió de opinión y ahora los valida con `ffprobe`, así que solo
// pasan WAV, FLAC, MP3 y M4A de verdad — no basta con la extensión.
export const AUDIO_MAX_BYTES = 200 * 1024 * 1024;

/**
 * Sube el audio de un envío ya registrado.
 *
 * 🔴 Va con `XMLHttpRequest` y no con `fetch`, y no es nostalgia: `fetch` no
 * informa del progreso de SUBIDA. Doscientos megas sin barra son varios
 * minutos de pantalla quieta, y quien está esperando no sabe si va o si se ha
 * colgado. `xhr.upload.onprogress` es la única forma de contarlo.
 *
 * 🔴 Igual que la carátula: si falla, **el envío del paso 1 ya está hecho**. No
 * se reintenta el paso 1 ni se dice que no llegó — llegó, y lo que falta es el
 * fichero. El panel de la radio ya avisa cuando un envío declaró audio que no
 * apareció.
 *
 * @param {(porcentaje: number) => void} [alProgresar] 0-100.
 * @returns {Promise<{ok: boolean, motivo?: string}>} Nunca lanza.
 */
export function subirAudio(id, fichero, alProgresar) {
  if (!id || !fichero) return Promise.resolve({ ok: false, motivo: "sin-datos" });

  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${URL_ENVIO}/${encodeURIComponent(id)}/audio`);
    xhr.setRequestHeader(
      "Content-Type",
      fichero.type || "application/octet-stream",
    );
    // El servidor corta a los 120s; se avisa antes para poder contarlo con
    // palabras en vez de dejar la barra colgada.
    xhr.timeout = 125000;

    if (typeof alProgresar === "function") {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          alProgresar(Math.round((e.loaded / e.total) * 100));
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) return resolve({ ok: true });
      if (xhr.status === 404) return resolve({ ok: false, motivo: "no-encontrado" });
      if (xhr.status === 429) return resolve({ ok: false, motivo: "demasiados" });
      // 400/500: el fichero no era el audio que decía ser. Lo dice `ffprobe`,
      // que mira dentro y no se fía de la extensión.
      if (xhr.status === 400 || xhr.status === 500)
        return resolve({ ok: false, motivo: "no-es-audio" });
      resolve({ ok: false, motivo: "servidor" });
    };
    xhr.onerror = () => resolve({ ok: false, motivo: "sin-conexion" });
    xhr.ontimeout = () => resolve({ ok: false, motivo: "tardo-demasiado" });
    xhr.onabort = () => resolve({ ok: false, motivo: "cancelado" });

    xhr.send(fichero);
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// El catálogo de géneros del buzón
// ═══════════════════════════════════════════════════════════════════════════
//
// Contrato: `CANAL_ABIERTO_WEB_INSTRUCCIONES.md` §4, actualizado el 02.09.26.
// Endpoint: `https://listen.astra.fm/radar/generos.json`, `max-age=3600`.
//
// 🔴 **El género deja de ser texto libre.** Antes se escribía a mano y llegaban
// «post punk», «Post-Punk» y «postpunk» diciendo lo mismo; ahora se elige de un
// catálogo de 89 y el servidor canonicaliza contra él.
//
// Trae dos listas:
//
//   · `casa`     los 19 que emite Astra. Van primero: es lo que más manda quien
//                escribe a este buzón, y ahorra buscar en una lista de 89.
//   · `generos`  el catálogo entero, los 19 de casa incluidos.
//
// ⚠️ Lo que **no** está en el catálogo no se pierde: el formulario deja escribir
// uno propio y viaja tal cual, en privado. La redacción decide los géneros
// públicos al publicar, como siempre.
const URL_GENEROS = `${BASE}/radar/generos.json`;

// La hora que manda el servidor. Es un catálogo, no un listado vivo.
const TTL_GENEROS = 60 * 60 * 1000;

let generosCache = null;
let generosAt = 0;

/**
 * `{ casa, generos }`. Nunca lanza: sin catálogo el formulario deja escribir el
 * género a mano, que es lo que hacía antes de esto.
 */
export async function getGeneros() {
  const ahora = Date.now();
  if (generosCache && ahora - generosAt <= TTL_GENEROS) return generosCache;

  let datos = { casa: [], generos: [] };

  try {
    const r = await fetch(URL_GENEROS, { headers: { Accept: "application/json" } });
    if (r.ok) {
      const json = await r.json();
      datos = {
        casa: Array.isArray(json?.casa) ? json.casa : [],
        generos: Array.isArray(json?.generos) ? json.generos : [],
      };
    }
  } catch (error) {
    // Sin catálogo, a mano. Ver arriba.
  }

  generosCache = datos;
  generosAt = ahora;
  return datos;
}
